import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { LabelComponent, RootDirective } from '@pure/hive';
import { smartTimer } from '@pstg/smart-timer';
import moment from 'moment';
import { EvercycleBuybackRenderInfo } from '../../../../data/models/recommendations.models';
import { EVERCYCLE_BUYBACK_CARD_PLUGIN_NAME } from './evercycle-plugin-names';
import { registerAsCardPlugin } from '../../services/recommendation-component-registry.service';
import { emitEvercycleEvent, EVERCYCLE_EVENTS } from './evercycle-amplitude';
import {
    EVERCYCLE_PENDING_LABEL,
    EVERCYCLE_PROGRAM_LABEL,
    EVERCYCLE_VALUATION_CAPTION,
    EVERCYCLE_VALUATION_QUALIFIER,
} from './evercycle-copy';

export { EVERCYCLE_BUYBACK_CARD_PLUGIN_NAME };

/**
 * Interval at which the actionability clock ticks. Thirty seconds is coarse
 * enough to be a non-issue for change detection, fine enough that a card
 * stays visibly "actionable" for at most a brief moment past expiry.
 */
export const EVERCYCLE_CLOCK_TICK_MS = 30_000;

/**
 * Card body for an EverCycle Buyback recommendation.
 *
 * Rendering rule (per CUJ 002 spec): a valuation that is null OR whose
 * `validUntilUtc` is at/past the current clock renders as "Valuation pending".
 * The clock is a signal that ticks every {@link EVERCYCLE_CLOCK_TICK_MS}
 * (plus a one-shot at the deadline, for cards opened before expiry) so a
 * card does not stay actionable indefinitely after the deadline.
 *
 * Emits `evercycle_card_impression` once per unique `recommendationId`
 * seen by this component instance (A → B → A emits one event for A).
 */
@Component({
    selector: 'evercycle-buyback-card-plugin',
    templateUrl: './evercycle-buyback-card-plugin.component.html',
    styleUrls: ['./evercycle-buyback-card-plugin.component.scss'],
    standalone: true,
    imports: [LabelComponent, RootDirective, CurrencyPipe, DatePipe],
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvercycleBuybackCardPluginComponent {
    renderInfo = input.required<EvercycleBuybackRenderInfo>();

    protected readonly programLabel = EVERCYCLE_PROGRAM_LABEL;
    protected readonly pendingLabel = EVERCYCLE_PENDING_LABEL;
    protected readonly valuationCaption = EVERCYCLE_VALUATION_CAPTION;
    protected readonly valuationQualifier = EVERCYCLE_VALUATION_QUALIFIER;

    /** Ticking clock signal; readers (`isActionable`) take a dependency on it so expiry is reactive. */
    private readonly nowMs = signal(Date.now());

    protected readonly isActionable = computed(() => {
        const info = this.renderInfo();
        return info.isValuationActionableAt(moment.utc(this.nowMs()));
    });

    protected readonly validUntilDate = computed(() => this.renderInfo().valuation?.validUntilUtc?.toDate());

    private readonly emittedForIds = new Set<string>();

    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        // Project convention (no-restricted-globals + no-restricted-properties):
        // use `@pstg/smart-timer` instead of raw setInterval / setTimeout.
        smartTimer(EVERCYCLE_CLOCK_TICK_MS, EVERCYCLE_CLOCK_TICK_MS)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.nowMs.set(Date.now()));

        effect(() => {
            const info = this.renderInfo();
            if (!info || this.emittedForIds.has(info.id)) {
                return;
            }
            this.emittedForIds.add(info.id);
            emitEvercycleEvent(EVERCYCLE_EVENTS.CARD_IMPRESSION, {
                recommendation_id: info.recommendationId,
                program: info.program,
                org_id: info.orgId,
            });
        });

        // One-shot smartTimer at the valuation deadline so a card opened
        // before expiry flips to pending at the exact deadline rather than
        // at the next periodic tick.
        effect(() => {
            const info = this.renderInfo();
            // Fire the one-shot at the server's acceptance deadline, not the
            // display-validity date, so a card that is still actionable
            // during the grace window flips to pending at the correct moment.
            const deadlineMoment = info.valuation?.actionableUntilUtc ?? info.valuation?.validUntilUtc;
            const expiryMs = deadlineMoment?.valueOf();
            if (!expiryMs) {
                return;
            }
            const delay = expiryMs - Date.now();
            if (delay <= 0 || delay > 2_147_483_647) {
                // Already past, or beyond the 32-bit timer cap — the
                // periodic poll handles both ends.
                return;
            }
            smartTimer(delay + 1)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.nowMs.set(Date.now()));
        });
    }
}

registerAsCardPlugin(EVERCYCLE_BUYBACK_CARD_PLUGIN_NAME, EvercycleBuybackCardPluginComponent);
